import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Plus } from "lucide-react";
import { formatDate } from "@/lib/constants";

export default function ProjectsTab({ clientId, canEdit }) {
  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [dlgProject, setDlgProject] = useState(false);
  const [dlgTask, setDlgTask] = useState(null);
  const [pForm, setPForm] = useState({ title: "", description: "", due_date: "" });
  const [tForm, setTForm] = useState({ title: "", description: "", due_date: "", priority: "Medium", assigned_to: "" });

  const load = async () => {
    const { data } = await api.get(`/clients/${clientId}/projects`);
    setProjects(data.projects || []);
    setTasks(data.tasks || []);
    try {
      const { data: us } = await api.get("/users");
      setUsers(us);
    } catch { /* non-admin advisors still get advisor+execution list */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [clientId]);

  const createProject = async () => {
    if (!pForm.title) return;
    try {
      await api.post("/projects", { client_id: clientId, ...pForm });
      toast.success("Project created");
      setDlgProject(false);
      setPForm({ title: "", description: "", due_date: "" });
      load();
    } catch { toast.error("Failed"); }
  };

  const createTask = async () => {
    if (!tForm.title || !dlgTask) return;
    try {
      await api.post("/tasks", {
        client_id: clientId,
        project_id: dlgTask.id,
        ...tForm,
        assigned_to: tForm.assigned_to || null,
      });
      toast.success("Task added");
      setDlgTask(null);
      setTForm({ title: "", description: "", due_date: "", priority: "Medium", assigned_to: "" });
      load();
    } catch { toast.error("Failed"); }
  };

  const moveTask = async (t, next) => {
    try {
      await api.patch(`/tasks/${t.id}`, { status: next });
      load();
    } catch { toast.error("Failed"); }
  };

  const COLS = ["To Do", "In Progress", "Blocked", "Done"];

  return (
    <div className="space-y-8">
      <div className="flex justify-end">
        {canEdit && (
          <Dialog open={dlgProject} onOpenChange={setDlgProject}>
            <DialogTrigger asChild>
              <Button size="sm" data-testid="add-project-btn"><Plus className="mr-1 h-3 w-3" /> New project</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Create project</DialogTitle></DialogHeader>
              <div className="space-y-3 py-3">
                <div><Label>Title</Label><Input value={pForm.title} onChange={(e) => setPForm({ ...pForm, title: e.target.value })} data-testid="project-title-input" /></div>
                <div><Label>Description</Label><Textarea rows={3} value={pForm.description} onChange={(e) => setPForm({ ...pForm, description: e.target.value })} data-testid="project-desc-input" /></div>
                <div><Label>Due date</Label><Input type="date" value={pForm.due_date} onChange={(e) => setPForm({ ...pForm, due_date: e.target.value })} data-testid="project-due-input" /></div>
              </div>
              <DialogFooter><Button onClick={createProject} data-testid="project-save-btn">Create</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {projects.length === 0 && <p className="text-sm text-muted-foreground">No projects yet. Create one to organise ad-hoc work — like a KYC refresh, a portfolio rebalance, or a document chase — outside the main journey stages.</p>}

      {projects.map((p) => {
        const pTasks = tasks.filter((t) => t.project_id === p.id);
        const groups = Object.fromEntries(COLS.map((c) => [c, pTasks.filter((t) => t.status === c)]));
        return (
          <Card key={p.id} className="border-border bg-white p-5" data-testid={`project-${p.id}`}>
            <div className="mb-1 flex items-start justify-between">
              <div>
                <div className="font-dashboard text-base font-bold">{p.title}</div>
                {p.description && <p className="mt-1 text-sm text-muted-foreground">{p.description}</p>}
                {p.due_date && <div className="mt-1 text-xs text-primary">Due · {formatDate(p.due_date)}</div>}
              </div>
              {canEdit && (
                <Button size="sm" variant="outline" onClick={() => setDlgTask(p)} data-testid={`add-task-${p.id}`}>
                  <Plus className="mr-1 h-3 w-3" /> Task
                </Button>
              )}
            </div>
            <div className="mt-4 grid gap-3 md:grid-cols-4">
              {COLS.map((col) => (
                <div key={col} className="rounded-lg border border-border bg-secondary/20 p-3" data-testid={`kanban-col-${col.replace(/\s+/g, "-").toLowerCase()}`}>
                  <div className="mb-2 flex items-center justify-between">
                    <div className="font-dashboard text-xs font-bold">{col}</div>
                    <span className="rounded-full bg-white px-2 py-0.5 text-[10px]">{groups[col].length}</span>
                  </div>
                  <div className="space-y-2">
                    {groups[col].map((t) => (
                      <div key={t.id} className="rounded-md border border-border bg-white p-2 text-xs" data-testid={`task-${t.id}`}>
                        <div className="font-medium">{t.title}</div>
                        {t.description && <div className="mt-0.5 text-muted-foreground">{t.description}</div>}
                        <div className="mt-1 flex items-center justify-between text-[10px] text-muted-foreground">
                          <span>{t.priority}</span>
                          {t.due_date && <span>{formatDate(t.due_date)}</span>}
                        </div>
                        {canEdit && col !== "Done" && (
                          <div className="mt-2 flex flex-wrap gap-1">
                            {COLS.filter((c) => c !== col).map((next) => (
                              <button
                                key={next}
                                onClick={() => moveTask(t, next)}
                                data-testid={`task-move-${t.id}-${next.replace(/\s+/g, "-").toLowerCase()}`}
                                className="rounded-md border border-border bg-white px-1.5 py-0.5 text-[10px] hover:bg-secondary"
                              >
                                → {next}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                    {groups[col].length === 0 && <p className="text-[10px] text-muted-foreground">—</p>}
                  </div>
                </div>
              ))}
            </div>
          </Card>
        );
      })}

      {dlgTask && (
        <Dialog open={!!dlgTask} onOpenChange={(o) => { if (!o) setDlgTask(null); }}>
          <DialogContent>
            <DialogHeader><DialogTitle>Add task · {dlgTask.title}</DialogTitle></DialogHeader>
            <div className="space-y-3 py-3">
              <div><Label>Title</Label><Input value={tForm.title} onChange={(e) => setTForm({ ...tForm, title: e.target.value })} data-testid="task-title-input" /></div>
              <div><Label>Description</Label><Textarea rows={2} value={tForm.description} onChange={(e) => setTForm({ ...tForm, description: e.target.value })} data-testid="task-desc-input" /></div>
              <div className="grid gap-3 md:grid-cols-2">
                <div>
                  <Label>Due</Label>
                  <Input type="date" value={tForm.due_date} onChange={(e) => setTForm({ ...tForm, due_date: e.target.value })} data-testid="task-due-input" />
                </div>
                <div>
                  <Label>Priority</Label>
                  <select className="mt-2 h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                    value={tForm.priority} onChange={(e) => setTForm({ ...tForm, priority: e.target.value })} data-testid="task-priority-input">
                    <option>Low</option><option>Medium</option><option>High</option>
                  </select>
                </div>
              </div>
              <div>
                <Label>Assign to</Label>
                <select className="mt-2 h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                  value={tForm.assigned_to} onChange={(e) => setTForm({ ...tForm, assigned_to: e.target.value })} data-testid="task-assignee-input">
                  <option value="">Unassigned</option>
                  {users.map((u) => <option key={u.id} value={u.id}>{u.name} · {u.role}</option>)}
                </select>
              </div>
            </div>
            <DialogFooter><Button onClick={createTask} disabled={!tForm.title} data-testid="task-save-btn">Add task</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
